import type {
	ActionItem,
	FinancingView,
	InventoryInformationView,
	ModelInformationView,
	PublishingRuleViolationsView,
	TechnicalDataView,
	VisibilityView,
} from "@slfinrtl/ui-bff-routes"
import type { PropsWithChildren } from "react"
import { useMemo, useState } from "react"
import { EditStageContextProvider } from "src/context/editStageContext/EditStageContextProvider"
import { VehicleDetailsContext } from "./VehicleDetailsContext"

export type Event =
	| "financingViewChanged"
	| "inventoryInformationViewChanged"
	| "inventoryInformationUpdated"
	| "modelInformationChanged"
	| "porscheApprovedEditActionChanged"
	| "publishingRuleViolationsChanged"
	| "saleCancelled"
	| "technicalDataChanged"
	| "visibilityChanged"
	| "visibilityUpdated"

interface Subscriber {
	callbackFn: (payload: any) => void
	event: Event
}

export type PayloadType<T extends Event> = T extends "inventoryInformationViewChanged" | "inventoryInformationUpdated"
	? InventoryInformationView | null
	: T extends "financingViewChanged"
		? FinancingView
		: T extends "modelInformationChanged"
			? ModelInformationView | null
			: T extends "technicalDataChanged"
				? TechnicalDataView | null
				: T extends "visibilityChanged" | "visibilityUpdated"
					? VisibilityView
					: T extends "porscheApprovedEditActionChanged" | "saleCancelled"
						? ActionItem | undefined
						: T extends "publishingRuleViolationsChanged"
							? PublishingRuleViolationsView
							: never

export interface NotifyMessage<T extends Event> {
	event: T
	payload: PayloadType<T>
}

interface VehicleAddContextProviderProps extends PropsWithChildren {
	inventoryId: string
}

export const VehicleDetailsContextProvider = ({ children, inventoryId }: VehicleAddContextProviderProps) => {
	const [subscribers, setSubscribers] = useState<Subscriber[]>()

	// subscribe the callbackFunction to the given events
	const subscribe = useMemo(
		() =>
			<T extends Event>(event: T, callbackFn: (payload: PayloadType<T>) => void) =>
				setSubscribers((values) =>
					values !== undefined ? [...values, { callbackFn, event }] : [{ callbackFn, event }],
				),
		[],
	)

	// execute all callbackFn for the notified events
	const notify = useMemo(
		() =>
			<T extends Event>(message: NotifyMessage<T>) => {
				if (subscribers === undefined) {
					return
				}
				subscribers
					.filter((sub) => message.event === sub.event)
					.forEach((sub) => {
						sub.callbackFn(message.payload)
					})
			},
		[subscribers],
	)

	return (
		<VehicleDetailsContext.Provider value={{ inventoryId, notify, subscribe }}>
			<EditStageContextProvider>{children}</EditStageContextProvider>
		</VehicleDetailsContext.Provider>
	)
}
